import { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import {
    ArrowLeft, Columns, FileSpreadsheet, AlertCircle, AlertTriangle, Inbox, Upload, Trash2, Loader2, Play, Download, Info, Users, CheckCircle2, Circle, Mail, Send,
} from 'lucide-react';
import { selectUser } from '../store/authSlice';
import { commissionReportsApi } from '../api/commissionReportsApi';
import RunErrorText from '../components/RunErrorText';
import ColumnMappingDrawer from '../components/ColumnMappingDrawer';
import PayeeDiscoveryReview from './PayeeDiscoveryReview';

const MONTH_LABELS = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December',
];

const monthLabel = (year, month) => `${MONTH_LABELS[month - 1]} ${year}`;

// File-kind values must match com.commissioner.service.CommissionRunService
// KIND_* constants exactly — the engine matches files by these strings and
// silently skips anything else. All four inputs are required for a run.
//
// `sheets` says which sheets of the workbook the browser converts and sends;
// it is shown under the row so a workbook missing one is explained before
// the conversion refuses it (see lib/workbookToCsv.js).
const FILE_KINDS = [
    { value: 'CRM',            label: 'CRM output',     required: true, sheets: 'first sheet' },
    { value: 'ADHOC',          label: 'Adhoc fees',     required: true, sheets: 'first sheet' },
    { value: 'LENDER_UPFRONT', label: 'Lender upfront', required: true, sheets: 'Inv_, Data_ and Claw_ sheets' },
    { value: 'LENDER_TRAIL',   label: 'Lender trail',   required: true, sheets: 'Inv_ and Data_ sheets' },
];

const KIND_LABEL = Object.fromEntries(FILE_KINDS.map((k) => [k.value, k.label]));

// Row background colours have to be clipped to the list's rounded corners
// somehow. The container can't do it with `overflow-hidden` — that also clips
// the CRM row's tooltip, which is taller than the rows below it — so the first
// and last rows round themselves instead.
const ROW_CORNERS = 'first:rounded-t-lg last:rounded-b-lg';

/**
 * Month detail. Shows input files (with upload + delete), a Run section
 * with a Run button (rates come from saved config), and the read-only
 * list of generated
 * reports.
 *
 * On first load: if no run exists for this month yet (404), we render an
 * empty state inviting the user to upload — the run is created server-side
 * when the first file arrives.
 */
export default function MonthDetailPage() {
    const { year, month } = useParams();
    const navigate = useNavigate();
    const user = useSelector(selectUser);
    const orgUuid = user?.organisationUuid;

    const yearNum = Number(year);
    const monthNum = Number(month);

    const [detail, setDetail] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // A run records payments against payees, so a subcontractor or referrer the
    // CRM names but the config does not know about is money attributed to
    // nobody. Reviewing is the only step that surfaces them, and it was easy to
    // walk past, so Run stays disabled until it has been opened for this month.
    //
    // Deliberately NOT persisted: it resets on every visit and on changing
    // month, because the CRM can be re-uploaded with new payees at any point
    // and a remembered "yes, reviewed" would be about the old file.
    const [payeesReviewed, setPayeesReviewed] = useState(false);
    useEffect(() => setPayeesReviewed(false), [orgUuid, yearNum, monthNum]);

    // Refresh whenever year/month/org changes.
    useEffect(() => {
        if (!orgUuid) return;
        let cancelled = false;
        loadDetail(orgUuid, yearNum, monthNum, cancelled, setDetail, setError, setLoading);
        return () => {
            cancelled = true;
        };
    }, [orgUuid, yearNum, monthNum]);

    // Local refresh helper used after uploads / deletes.
    const refresh = () => {
        if (!orgUuid) return;
        loadDetail(orgUuid, yearNum, monthNum, false, setDetail, setError, setLoading);
    };

    // Re-read the month WITHOUT the loading state. `refresh` hides every
    // section while it loads, which unmounts them — and with them any dialog
    // they are showing. After emailing an RCTI the section has to stay on
    // screen with its "sent" notice while the row underneath picks up the
    // server's record of the send.
    const refreshQuietly = () => {
        if (!orgUuid) return;
        commissionReportsApi.getMonth(orgUuid, yearNum, monthNum)
            .then(setDetail)
            .catch(() => { /* the next full refresh will report it */ });
    };

    // After a run, the API returns the updated detail directly — feed it
    // straight into state so the new output file appears without a
    // second GET.
    const handleRunComplete = (updated) => setDetail(updated);

    const inputFiles = detail?.inputFiles ?? [];
    const hasCrm = inputFiles.some((f) => f.kind === 'CRM');
    // All four input kinds are required before a run can be started.
    const hasRequiredInputs = FILE_KINDS
        .filter((k) => k.required)
        // A file uploaded before browser-side conversion holds only the
        // workbook, which the server no longer opens: present, not runnable.
        .every((k) => inputFiles.some((f) => f.kind === k.value && f.csv !== false));

    return (
        <div className="px-8 py-8 max-w-5xl">
            <button
                onClick={() => navigate(`/commission-reports?year=${year}`)}
                className="flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-900 mb-4"
            >
                <ArrowLeft size={14} />
                Back to {year}
            </button>

            <div className="mb-6">
                <h2 className="font-display text-2xl font-semibold text-slate-900">
                    {monthLabel(yearNum, monthNum)}
                </h2>
                <p className="text-sm text-slate-500 mt-1">
                    {detail?.generatedAt
                        ? `Run completed ${formatDateTime(detail.generatedAt)}`
                        : detail
                            ? 'Not yet run'
                            : '—'}
                </p>
            </div>

            {loading && <div className="text-sm text-slate-400">Loading…</div>}

            {error && !loading && (
                <div className="p-3 bg-red-50 border border-red-200 rounded-md flex items-start gap-2 text-sm text-red-700">
                    <AlertCircle size={16} className="mt-0.5 flex-shrink-0" />
                    <span>{error}</span>
                </div>
            )}

            {!loading && (
                <>
                    <InputFilesSection
                        orgUuid={orgUuid}
                        year={yearNum}
                        month={monthNum}
                        files={detail?.inputFiles ?? []}
                        onChange={refresh}
                    />

                    <div className="h-6" />

                    <PayeeReviewSection
                        orgUuid={orgUuid}
                        year={yearNum}
                        month={monthNum}
                        hasCrm={hasCrm}
                        reviewed={payeesReviewed}
                        onReviewed={() => setPayeesReviewed(true)}
                    />

                    <div className="h-6" />

                    <RunSection
                        orgUuid={orgUuid}
                        year={yearNum}
                        month={monthNum}
                        canRun={hasRequiredInputs}
                        payeesReviewed={payeesReviewed}
                        currentStatus={detail?.status}
                        failureReason={detail?.failureReason}
                        onRunComplete={handleRunComplete}
                        onCleaned={refresh}
                    />

                    <div className="h-6" />

                    <OutputFilesSection
                        orgUuid={orgUuid}
                        year={yearNum}
                        month={monthNum}
                        files={detail?.outputFiles ?? []}
                        status={detail?.status}
                        onChange={refreshQuietly}
                    />
                </>
            )}
        </div>
    );
}

// ── Loaders ────────────────────────────────────────────────────────────

function loadDetail(orgUuid, year, month, cancelled, setDetail, setError, setLoading) {
    setLoading(true);
    setError(null);
    commissionReportsApi
        .getMonth(orgUuid, year, month)
        .then((d) => {
            if (cancelled) return;
            setDetail(d);
        })
        .catch((err) => {
            if (cancelled) return;
            if (err.response?.status === 404) {
                // Empty state — no run record yet. Treat as if the month exists with empty file lists.
                setDetail({ year, month, status: 'NO_DATA', inputFiles: [], outputFiles: [] });
            } else {
                setError(err.response?.data?.message ?? err.message);
            }
        })
        .finally(() => !cancelled && setLoading(false));
}

// ── Input files (uploadable) ───────────────────────────────────────────

function InputFilesSection({ orgUuid, year, month, files, onChange }) {
    const [uploadError, setUploadError] = useState(null);
    // Which file's column mapping is open, if any. One at a time: the mapping
    // is one document and two drawers editing it would each save over the other.
    const [mappingFor, setMappingFor] = useState(null);
    const [mappingVersion, setMappingVersion] = useState(null);
    // Which kind is currently uploading, so only that row shows a spinner.
    const [uploadingKind, setUploadingKind] = useState(null);
    // Loan-rate importing, offered on the CRM row only.
    const [seeding, setSeeding] = useState(false);
    const [seedResult, setSeedResult] = useState(null);
    const [seedError, setSeedError] = useState(null);
    const [confirmImportOpen, setConfirmImportOpen] = useState(false);
    // The file awaiting delete confirmation: { id, label, name }.
    const [pendingDelete, setPendingDelete] = useState(null);
    const [deletingId, setDeletingId] = useState(null);

    // Import the CRM's split rates and payee names into the loan rate store
    // without running the month. Confirmed first: it writes to the store every
    // future run reads, and the operator may have meant to press Run.
    const handlePopulateRates = async () => {
        setConfirmImportOpen(false);
        setSeedError(null);
        setSeedResult(null);
        setSeeding(true);
        try {
            setSeedResult(await commissionReportsApi.populateLoanRates(orgUuid, year, month));
            // The run's status is intentionally unchanged by importing, but the
            // parent refreshes anyway so nothing else on the page goes stale.
            onChange();
        } catch (err) {
            setSeedError(err.response?.data?.message ?? err.message);
        } finally {
            setSeeding(false);
        }
    };

    // Upload one file tagged with a specific kind. Each KindRow calls this
    // with its own kind, so the upload control lives on the row itself —
    // no shared dropdown to set first.
    const handleUpload = async (kindValue, file) => {
        if (!file) return;
        setUploadError(null);
        setUploadingKind(kindValue);
        try {
            await commissionReportsApi.uploadInputFile(orgUuid, year, month, file, kindValue);
            onChange();
        } catch (err) {
            setUploadError(err.response?.data?.message ?? err.message);
        } finally {
            setUploadingKind(null);
        }
    };

    // Deletion is confirmed through an in-app dialog, not window.confirm: the
    // native one is unstyled, cannot name the file or its kind, and is
    // suppressible per-site — a "don't show again" click would make deletes
    // silent and instant.
    const handleDelete = async () => {
        const target = pendingDelete;
        setPendingDelete(null);
        if (!target) return;
        setUploadError(null);
        setDeletingId(target.id);
        try {
            await commissionReportsApi.deleteInputFile(orgUuid, year, month, target.id);
            onChange();
        } catch (err) {
            setUploadError(err.response?.data?.message ?? err.message);
        } finally {
            setDeletingId(null);
        }
    };

    return (
        <section>
            <div className="mb-3">
                <h3 className="font-display text-base font-semibold text-slate-900">
                    Input files
                </h3>
                <p className="text-xs text-slate-500">
                    All four input files are required to run. Choose each workbook
                    (.xlsx) as you received it: your browser converts the sheets the
                    engine reads into CSV from the stored cell values and uploads
                    those, so nothing is lost to display formatting and the server
                    never opens the workbook. For a historical month that was already
                    paid outside the system, upload the CRM alone and use{' '}
                    <span className="font-medium text-amber-700">Import rate
                    splits</span> to record its splits without running it — later
                    months need them to price trail and to reverse clawbacks.
                </p>
            </div>

            {uploadError && (
                <div className="mb-3 p-3 bg-red-50 border border-red-200 rounded-md flex items-start gap-2 text-sm text-red-700">
                    <AlertCircle size={16} className="mt-0.5 flex-shrink-0" />
                    <span>{uploadError}</span>
                </div>
            )}

            {seedError && (
                <div className="mb-3 p-3 bg-red-50 border border-red-200 rounded-md flex items-start gap-2 text-sm text-red-700">
                    <AlertCircle size={16} className="mt-0.5 flex-shrink-0" />
                    <span>{seedError}</span>
                </div>
            )}

            {seedResult && <SeedResultNotice result={seedResult} />}

            {/* One row per required file kind: grayed with an Upload button when
                missing, green with download + delete once uploaded. Each row
                uploads its own kind.

                NOT `overflow-hidden`, deliberately: the CRM row's tooltip is
                taller than the rows beneath it, and clipping it to this box cut
                the bottom off. The rounded corners the clip used to provide are
                done on the first/last row instead (see KindRow), which contains
                the row background colours without trapping the tooltip. */}
            <div className="bg-white border border-slate-200 rounded-lg">
                <ul className="divide-y divide-slate-100">
                    {FILE_KINDS.map((k) => {
                        const file = files.find((f) => f.kind === k.value) ?? null;
                        return (
                            <KindRow
                                key={k.value}
                                kindValue={k.value}
                                label={k.label}
                                sheets={k.sheets}
                                required={k.required}
                                file={file}
                                uploading={uploadingKind === k.value}
                                anyUploading={uploadingKind !== null}
                                onUpload={(f) => handleUpload(k.value, f)}
                                onDownload={file ? () => downloadFileAndSave(
                                    orgUuid, year, month, file.id, file.name) : null}
                                onDelete={file ? () => setPendingDelete({
                                    id: file.id, label: k.label, name: file.name }) : null}
                                deleting={file != null && deletingId === file.id}
                                // Importing rate splits belongs to the CRM alone
                                // — it is the only file carrying split rates and
                                // payee names.
                                onPopulateRates={
                                    k.value === 'CRM' && file
                                        ? () => setConfirmImportOpen(true)
                                        : null}
                                populating={seeding}
                                onEditMapping={() => setMappingFor(k)}
                                mappingVersion={mappingVersion}
                                // The click that opens the dialog also focuses
                                // the button, which would hold the tooltip open
                                // behind the dialog's scrim.
                                hideImportTooltip={confirmImportOpen || seeding}
                            />
                        );
                    })}
                </ul>
            </div>

            <ConfirmImportSplitsDialog
                open={confirmImportOpen}
                monthLabel={monthLabel(year, month)}
                onCancel={() => setConfirmImportOpen(false)}
                onConfirm={handlePopulateRates}
            />

            <ConfirmDeleteFileDialog
                file={pendingDelete}
                monthLabel={monthLabel(year, month)}
                onCancel={() => setPendingDelete(null)}
                onConfirm={handleDelete}
            />

            {mappingFor && (
                <ColumnMappingDrawer
                    orgUuid={orgUuid}
                    year={year}
                    month={month}
                    fileType={mappingFor.value}
                    fileLabel={mappingFor.label}
                    hasFile={files.some((f) => f.kind === mappingFor.value)}
                    onClose={() => setMappingFor(null)}
                    onSaved={(saved) => setMappingVersion(saved.version)}
                />
            )}
        </section>
    );
}

/**
 * Opens the column mapping for this file.
 *
 * Sits on the row rather than in a settings page because that is where the
 * question arises: a run has just failed naming a column, and the file it names
 * is the row you are looking at.
 *
 * Shows the version in force so a mapping that has been changed is visible
 * without opening anything — the alternative is a month reading its files in a
 * way nobody on the screen can see.
 */
function MappingLink({ onClick, version, muted }) {
    if (!onClick) return null;
    return (
        <button
            onClick={onClick}
            title="Which columns of this file supply which values"
            className={`flex items-center gap-1 px-2 py-1 text-xs rounded-md whitespace-nowrap
                ${muted ? 'text-slate-400 hover:text-slate-600' : 'text-slate-500 hover:text-slate-800'}
                hover:bg-slate-100`}
        >
            <Columns size={13} />
            {version ? `Mapping v${version}` : 'Columns'}
        </button>
    );
}

/**
 * Outcome of a rates-only seed. Spells out that no commission was calculated —
 * the operator has just pressed a button on a month that still says "Pending",
 * and needs to know that is correct rather than a failure.
 *
 * Also names what the stored rates unlock (trail pricing AND clawback reversal),
 * because the seed produces no visible output of its own: without saying so, a
 * successful seed looks like it did nothing.
 */
function SeedResultNotice({ result }) {
    const { loansInCrm, ratesInserted, ratesUpdated, ratesBackdated, loansStored } = result;
    return (
        <div className="mb-3 p-3 bg-emerald-50 border border-emerald-200 rounded-md flex items-start gap-2 text-sm text-emerald-800">
            <CheckCircle2 size={16} className="mt-0.5 flex-shrink-0" />
            <div>
                <p className="font-medium">
                    Rate splits stored for {loansInCrm} loan
                    {loansInCrm === 1 ? '' : 's'} — no commission run executed.
                </p>
                <p className="text-xs mt-1 text-emerald-700">
                    {ratesInserted} new, {ratesUpdated} restated
                    {ratesBackdated > 0 && `, ${ratesBackdated} re-dated to this earlier month`}
                    {' · '}{loansStored} loan{loansStored === 1 ? '' : 's'} now in the rate store.
                    This month stays Pending because nothing was calculated or paid for it.
                </p>
                <p className="text-xs mt-1 text-emerald-700">
                    These loans can now be priced for trail in later months, and any
                    clawback against them can be reversed at the splits they were
                    originally paid at.
                </p>
            </div>
        </div>
    );
}

/**
 * A fixed row for one input-file kind. Two states:
 *   • uploaded — green check, filename + size/date, download + delete actions;
 *   • missing  — grayed out, hollow circle, "Not uploaded yet", and an Upload
 *     button that uploads straight into this kind.
 * Showing all kinds (not just uploaded ones) makes it obvious what's still
 * outstanding before a run.
 *
 * `onPopulateRates` is passed for the CRM row only, and only once a file is
 * present: it seeds the loan rate store from this CRM without running the
 * month, which is how a book older than the system gets priced.
 */
function KindRow({ kindValue, label, sheets, required, file, uploading, anyUploading,
                     onUpload, onDownload, onDelete, deleting,
                     onPopulateRates, populating, hideImportTooltip,
                     onEditMapping, mappingVersion }) {
    const [downloading, setDownloading] = useState(false);
    const fileInputRef = useRef(null);

    const handlePick = () => fileInputRef.current?.click();

    const handleFileChange = (e) => {
        const f = e.target.files?.[0];
        // Reset so the same file can be re-picked after a failure.
        e.target.value = '';
        if (f) onUpload(f);
    };

    const handleDownload = async () => {
        if (!onDownload || downloading) return;
        setDownloading(true);
        try {
            await onDownload();
        } catch (err) {
            // eslint-disable-next-line no-console
            console.error('File download failed:', err);
        } finally {
            setDownloading(false);
        }
    };

    if (!file) {
        return (
            <li className={`px-4 py-3 flex items-center gap-3 bg-slate-50 ${ROW_CORNERS}`}>
                <Circle size={18} className="text-slate-300 flex-shrink-0" />
                <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-slate-400">{label}</p>
                    <p className="text-xs text-slate-400 mt-0.5">
                        Not uploaded yet · the workbook's {sheets} will be converted
                    </p>
                </div>
                <MappingLink onClick={onEditMapping} version={mappingVersion} muted />
                {required && (
                    <span className="text-xs text-slate-400 font-medium whitespace-nowrap">
                        Required
                    </span>
                )}
                <button
                    onClick={handlePick}
                    disabled={anyUploading}
                    className="flex items-center gap-1.5 px-2.5 py-1.5 text-sm font-medium text-white bg-brand-600 hover:bg-brand-700 disabled:opacity-50 disabled:cursor-not-allowed rounded-md whitespace-nowrap"
                    title={`Upload ${label}`}
                >
                    {uploading
                        ? <Loader2 size={14} className="animate-spin" />
                        : <Upload size={14} />}
                    {uploading ? 'Uploading…' : 'Upload'}
                </button>
                <input
                    ref={fileInputRef}
                    type="file"
                    accept=".xlsx"
                    onChange={handleFileChange}
                    className="hidden"
                />
            </li>
        );
    }

    return (
        <li className={`px-4 py-3 flex items-center gap-3 bg-emerald-50/60 ${ROW_CORNERS}`}>
            <CheckCircle2 size={18} className="text-emerald-600 flex-shrink-0" />
            <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-slate-900 truncate">
                    {label}
                    <span className="text-slate-400 font-normal"> · {file.name}</span>
                </p>
                <p className="text-xs text-slate-500 mt-0.5">
                    {formatBytes(file.sizeBytes)} · {formatDateTime(file.timestamp)}
                </p>
                {file.csv === false && (
                    // Uploaded before browser-side conversion: the server holds
                    // the workbook but no longer opens one, so this month cannot
                    // run, map or import from it until it is uploaded again.
                    <p className="text-xs text-amber-700 mt-1">
                        Uploaded before conversion — upload the workbook again to run this month.
                    </p>
                )}
            </div>
            <MappingLink onClick={onEditMapping} version={mappingVersion} />
            {onPopulateRates && (
                <ImportRateSplitsButton
                    onClick={onPopulateRates}
                    busy={populating}
                    tooltipHidden={hideImportTooltip}
                />
            )}
            {onDownload && (
                <button
                    onClick={handleDownload}
                    disabled={downloading}
                    className="text-slate-400 hover:text-brand-600 p-1 rounded disabled:opacity-40 disabled:cursor-not-allowed"
                    title="Download file"
                >
                    {downloading
                        ? <Loader2 size={16} className="animate-spin" />
                        : <Download size={16} />}
                </button>
            )}
            {onDelete && (
                <button
                    onClick={onDelete}
                    disabled={deleting}
                    className="text-slate-400 hover:text-red-600 p-1 rounded disabled:opacity-40 disabled:cursor-not-allowed"
                    title="Delete file"
                >
                    {deleting
                        ? <Loader2 size={16} className="animate-spin" />
                        : <Trash2 size={16} />}
                </button>
            )}
        </li>
    );
}

/**
 * Amber "Import rate splits" action on the CRM row, with a described tooltip.
 *
 * Named for what it is — an IMPORT that fixes historical data — not "trail rates
 * only", which was wrong twice over: it imports rather than saves anything the
 * operator typed, and it stores the UPFRONT splits as well as the trail ones.
 * The upfront half is what a clawback reversal reads, so calling it trail-only
 * hid half its purpose.
 *
 * Amber rather than the usual brand blue because this is a corrective action on
 * historical data, not part of the normal monthly flow — it writes to the shared
 * loan rate store and produces no reports, so it should read as "careful, this
 * is the exception" rather than as the obvious next step. The warning icon is
 * doing the same job.
 *
 * <p><b>Tooltip opens downward, and the list must not clip it.</b> The CRM row is
 * the first of four and the tooltip is taller than the three rows beneath it, so
 * `overflow-hidden` on the list container cut the bottom off. That container no
 * longer clips; the row backgrounds are rounded per-row via `ROW_CORNERS`
 * instead. If more content is ever added here, check it still fits on a short
 * viewport — nothing repositions this tooltip automatically.
 *
 * <p>Hover and keyboard focus both open it, and `aria-describedby` ties it to the
 * button so screen readers get the description rather than just the label.
 *
 * <p><b>`tooltipHidden` unmounts the tooltip rather than restyling it.</b>
 * Clicking the button focuses it, and `group-focus-within` keeps the tooltip open
 * — so once the confirmation dialog opened, the tooltip lingered underneath the
 * dialog's scrim (dialog `z-50` over tooltip `z-20`), reading as a rendering bug.
 * Raising the dialog's z-index would not help: the tooltip should be gone the
 * moment the question is asked, not merely covered.
 */
function ImportRateSplitsButton({ onClick, busy, tooltipHidden }) {
    const tooltipId = 'import-rate-splits-tip';
    return (
        <div className="relative group flex-shrink-0">
            <button
                onClick={onClick}
                disabled={busy}
                aria-describedby={tooltipHidden ? undefined : tooltipId}
                className="flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium text-white bg-amber-600 hover:bg-amber-700 focus:outline-none focus:ring-2 focus:ring-amber-500 focus:ring-offset-1 disabled:opacity-50 disabled:cursor-not-allowed rounded-md whitespace-nowrap"
            >
                {busy
                    ? <Loader2 size={13} className="animate-spin" />
                    : <AlertTriangle size={13} />}
                {busy ? 'Importing splits…' : 'Import rate splits'}
            </button>

            {!tooltipHidden && (
            <div
                id={tooltipId}
                role="tooltip"
                className="pointer-events-none absolute right-0 top-full mt-2 w-80 z-20 opacity-0 invisible group-hover:opacity-100 group-hover:visible group-focus-within:opacity-100 group-focus-within:visible transition-opacity duration-100 rounded-md border border-amber-200 bg-white p-3 shadow-lg"
            >
                <p className="text-xs font-semibold text-amber-800 flex items-start gap-1.5">
                    <AlertTriangle size={13} className="mt-0.5 flex-shrink-0" />
                    Imports rate splits only — does not run this month
                </p>
                <p className="text-xs text-slate-600 mt-1.5 leading-relaxed">
                    A correction for a month that settled before this system and was
                    already paid elsewhere. It imports this CRM&rsquo;s upfront and
                    trail split rates, plus the sub-contractor and referrer names,
                    into the loan rate store — the only place a loan&rsquo;s splits are
                    ever recorded, since the CRM lists each loan just once.
                </p>
                <p className="text-xs font-medium text-slate-700 mt-2">
                    Later runs need them, e.g.:
                </p>
                <ul className="text-xs text-slate-600 mt-1 space-y-1 leading-relaxed list-disc pl-4">
                    <li>
                        <span className="font-medium text-slate-700">Trail</span> — a
                        run stops with &ldquo;no CRM row this month and no stored
                        rates&rdquo;. Import the month each named loan first appeared,
                        then re-run.
                    </li>
                    <li>
                        <span className="font-medium text-slate-700">Clawbacks</span> —
                        a discharge lands months after settlement, so with nothing
                        stored the reclaim is reported{' '}
                        <span className="font-medium text-slate-700">unmatched</span>{' '}
                        and nothing is recovered from the sub-contractor or referrer.
                    </li>
                </ul>
                <p className="text-xs text-slate-500 mt-1.5 leading-relaxed">
                    No reports, invoices or payments are produced, and the month stays
                    <span className="font-medium"> Pending</span>. Safe to repeat —
                    an already-stored loan is restated in place. Order does not matter.
                </p>
            </div>
            )}
        </div>
    );
}

// ── Payee review (discover + confirm from CRM) ─────────────────────────

/**
 * Offers a review of the payees and offices named in the uploaded CRM that
 * aren't yet in the org's config, opening the {@link PayeeDiscoveryReview}
 * modal. Only actionable once a CRM file is present — the discovery endpoint
 * has nothing to scan otherwise. Sits between upload and run so the user can
 * populate config before the run records payments against payees.
 */
function PayeeReviewSection({ orgUuid, year, month, hasCrm, reviewed, onReviewed }) {
    const [open, setOpen] = useState(false);

    return (
        <section>
            <div className="flex items-end justify-between mb-3">
                <div>
                    <h3 className="font-display text-base font-semibold text-slate-900">
                        Payees
                    </h3>
                    <p className="text-xs text-slate-500">
                        Add subcontractors and referrers from your CRM to your configuration.
                    </p>
                    {/* The Run button is gated on this, so say so rather than
                        leaving the operator to work out why Run is greyed. */}
                    <p className={`text-xs mt-1 flex items-center gap-1 ${
                        reviewed ? 'text-emerald-700' : 'text-amber-700'}`}>
                        {reviewed
                            ? <><CheckCircle2 size={12} /> Reviewed — Run is enabled</>
                            : <><Circle size={12} /> Review before running</>}
                    </p>
                </div>
                <button
                    onClick={() => { onReviewed?.(); setOpen(true); }}
                    disabled={!hasCrm}
                    title={hasCrm
                        ? 'Review payees found in the CRM'
                        : 'Upload a CRM output file first'}
                    className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-white bg-brand-600 hover:bg-brand-700 disabled:opacity-50 disabled:cursor-not-allowed rounded-md"
                >
                    <Users size={14} />
                    Review payees
                </button>
            </div>

            {!hasCrm && (
                <div className="p-3 bg-slate-50 border border-slate-200 rounded-md text-sm text-slate-600">
                    Upload the CRM output workbook to discover the payees it references.
                </div>
            )}

            <PayeeDiscoveryReview
                orgUuid={orgUuid}
                year={year}
                month={month}
                open={open}
                onClose={() => setOpen(false)}
            />
        </section>
    );
}

// ── Run (rates + execute button) ───────────────────────────────────────

/**
 * Run section: an info banner explaining that rates come from the saved
 * rate config, plus a Run button. Disabled until the user has uploaded
 * both required inputs (CRM + ADHOC). After a successful run, the parent
 * gets the new run state and the output file appears in the section below.
 *
 * Commission rates are not entered per-run here. They're resolved from the
 * organisation's saved rate config (Configuration → Rates). The run sends
 * no rates; the backend looks them up. If no config is saved, the backend
 * returns a 400 and the message is shown inline.
 */
function RunSection({ orgUuid, year, month, canRun, payeesReviewed, currentStatus,
                     failureReason, onRunComplete, onCleaned }) {
    const [running, setRunning] = useState(false);
    const [runError, setRunError] = useState(null);
    const [warnings, setWarnings] = useState([]);
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [cleanConfirmOpen, setCleanConfirmOpen] = useState(false);
    const [cleaning, setCleaning] = useState(false);
    const [cleanError, setCleanError] = useState(null);

    // A run exists for this month if it has been generated or attempted —
    // anything other than the synthetic NO_DATA empty state.
    const runExists = currentStatus && currentStatus !== 'NO_DATA';

    // Opening the in-app confirmation dialog is the entry point — the actual
    // run only fires once the user confirms in the dialog (runConfirmed).
    const handleRun = () => setConfirmOpen(true);

    const runConfirmed = async () => {
        setConfirmOpen(false);
        setRunError(null);
        setWarnings([]);
        setRunning(true);
        try {
            // No rates passed — the backend uses the org's saved rate config.
            const updated = await commissionReportsApi.executeRun(
                orgUuid, year, month);
            onRunComplete(updated);
            // executeRun can succeed at the HTTP layer but still come back
            // with status=FAILED (e.g. missing inputs detected server-side
            // or a malformed xlsx). Surface that as an inline error.
            if (updated.status === 'FAILED') {
                setRunError(updated.failureReason ?? 'Run failed.');
            } else if (updated.warnings?.length) {
                // Non-fatal: the run completed but the backend flagged
                // something worth the operator's attention (e.g. CRM rows
                // with a Settled Date outside the run month).
                setWarnings(updated.warnings);
            }
        } catch (err) {
            setRunError(err.response?.data?.message ?? err.message);
        } finally {
            setRunning(false);
        }
    };

    const cleanConfirmed = async () => {
        setCleanConfirmOpen(false);
        setCleanError(null);
        setCleaning(true);
        try {
            await commissionReportsApi.cleanRunData(orgUuid, year, month);
            // Wipe local run state and reload — the month reverts to the
            // empty (NO_DATA) state since the run record is gone.
            setRunError(null);
            setWarnings([]);
            onCleaned?.();
        } catch (err) {
            setCleanError(err.response?.data?.message ?? err.message);
        } finally {
            setCleaning(false);
        }
    };

    return (
        <section>
            <div className="flex items-end justify-between mb-3">
                <div>
                    <h3 className="font-display text-base font-semibold text-slate-900">
                        Run
                    </h3>
                    <p className="text-xs text-slate-500">
                        Calculates commissions from the converted input files using
                        your saved base rates, and produces an output workbook.
                    </p>
                </div>
                <button
                    onClick={handleRun}
                    disabled={!canRun || !payeesReviewed || running}
                    title={
                        !canRun
                            ? 'Upload all four input files first'
                            : !payeesReviewed
                                ? 'Review payees first — the run pays them'
                                : 'Run the commission calculation'
                    }
                    className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-white bg-brand-600 hover:bg-brand-700 disabled:opacity-50 disabled:cursor-not-allowed rounded-md"
                >
                    {running ? <Loader2 size={14} className="animate-spin" /> : <Play size={14} />}
                    {running ? 'Running…' : currentStatus === 'COMPLETED' ? 'Re-run' : 'Run'}
                </button>
            </div>

            {!canRun && (
                <div className="mb-3 p-3 bg-slate-50 border border-slate-200 rounded-md text-sm text-slate-600">
                    Upload all four input files (CRM output, Adhoc fees, Lender upfront, Lender trail) before running.
                </div>
            )}

            {/* Inputs are all present but the payees have not been looked at.
                Amber rather than grey: this is a step being skipped, not a
                prerequisite still loading. */}
            {canRun && !payeesReviewed && (
                <div className="mb-3 p-3 bg-amber-50 border border-amber-200 rounded-md flex items-start gap-2 text-sm text-amber-800">
                    <AlertTriangle size={16} className="mt-0.5 flex-shrink-0" />
                    <span>
                        Open <strong>Review payees</strong> above before running. The run records
                        payments against subcontractors and referrers named in the CRM — any that
                        are missing from your configuration are money attributed to nobody.
                    </span>
                </div>
            )}

            {/* Show the most recent failure reason from a previous run so
                the user can see why their last attempt didn't work even
                after navigating away and back. */}
            {!runError && currentStatus === 'FAILED' && failureReason && (
                <div className="mb-3 p-3 bg-red-50 border border-red-200 rounded-md flex items-start gap-2 text-sm text-red-700">
                    <AlertCircle size={16} className="mt-0.5 flex-shrink-0" />
                    <div>
                        <p className="font-medium">Last run failed</p>
                        <p className="mt-0.5"><RunErrorText text={failureReason} /></p>
                    </div>
                </div>
            )}

            {runError && (
                <div className="mb-3 p-3 bg-red-50 border border-red-200 rounded-md flex items-start gap-2 text-sm text-red-700">
                    <AlertCircle size={16} className="mt-0.5 flex-shrink-0" />
                    <span><RunErrorText text={runError} /></span>
                </div>
            )}

            {warnings.length > 0 && (
                <div className="mb-3 p-3 bg-amber-50 border border-amber-200 rounded-md flex items-start gap-2 text-sm text-amber-800">
                    <AlertTriangle size={16} className="mt-0.5 flex-shrink-0" />
                    <div>
                        <p className="font-medium">
                            Run completed with {warnings.length === 1 ? 'a warning' : 'warnings'}
                        </p>
                        <ul className="mt-0.5 space-y-0.5 list-disc list-inside">
                            {warnings.map((w, i) => (
                                <li key={i}>{w}</li>
                            ))}
                        </ul>
                        <p className="mt-1.5">
                            If you think this is a mistake, clean the run data with the{' '}
                            <span className="font-medium">“Clean run data”</span> button below, then
                            do the commission run in the correct month.
                        </p>
                    </div>
                </div>
            )}

            <div className="p-3 bg-blue-50 border border-blue-200 rounded-md flex items-start gap-2 text-sm text-blue-800">
                <Info size={16} className="mt-0.5 flex-shrink-0" />
                <div>
                    <p className="font-medium">Rates come from your saved configuration</p>
                    <p className="mt-0.5 text-blue-700">
                        This run uses the base commission rates saved under{' '}
                        <a href="/configuration" className="underline hover:text-blue-900">
                            Configuration
                        </a>
                        . Update them there to change what this run pays.
                    </p>
                </div>
            </div>

            {runExists && (
                <div className="mt-3 pt-3 border-t border-slate-200 flex items-center justify-between">
                    <div>
                        <p className="text-sm font-medium text-slate-900">Clean run data</p>
                        <p className="text-xs text-slate-500">
                            Deletes this run, its uploaded and generated files, and its payment
                            summaries. Subcontractors and referrers are not affected.
                        </p>
                    </div>
                    <button
                        onClick={() => { setCleanError(null); setCleanConfirmOpen(true); }}
                        disabled={cleaning || running}
                        className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-red-700 border border-red-300 hover:bg-red-50 disabled:opacity-50 disabled:cursor-not-allowed rounded-md flex-shrink-0"
                    >
                        {cleaning ? <Loader2 size={14} className="animate-spin" /> : <Trash2 size={14} />}
                        {cleaning ? 'Cleaning…' : 'Clean run data'}
                    </button>
                </div>
            )}

            {cleanError && (
                <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-md flex items-start gap-2 text-sm text-red-700">
                    <AlertCircle size={16} className="mt-0.5 flex-shrink-0" />
                    <span>{cleanError}</span>
                </div>
            )}

            <ConfirmRunDialog
                open={confirmOpen}
                monthLabel={monthLabel(year, month)}
                onCancel={() => setConfirmOpen(false)}
                onConfirm={runConfirmed}
            />

            <ConfirmCleanDialog
                open={cleanConfirmOpen}
                monthLabel={monthLabel(year, month)}
                onCancel={() => setCleanConfirmOpen(false)}
                onConfirm={cleanConfirmed}
            />
        </section>
    );
}

/**
 * In-app confirmation dialog shown before generating a pay run. Replaces the
 * native window.confirm so the month can be highlighted and the styling stays
 * consistent with the app. Renders nothing when {@code open} is false.
 *
 * <p>Closes on backdrop click or Cancel; Confirm fires {@code onConfirm}. The
 * month is rendered in bold light-red to draw the eye to which period is about
 * to be generated.
 */
function ConfirmRunDialog({ open, monthLabel, onCancel, onConfirm }) {
    if (!open) return null;
    return (
        <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-4"
            onClick={onCancel}
            role="presentation"
        >
            <div
                className="w-full max-w-md bg-amber-50 rounded-lg shadow-xl border border-amber-200 p-5"
                onClick={(e) => e.stopPropagation()}
                role="dialog"
                aria-modal="true"
                aria-labelledby="confirm-run-title"
            >
                <div className="flex items-start gap-3">
                    <AlertTriangle size={20} className="mt-0.5 flex-shrink-0 text-amber-500" />
                    <div>
                        <h4
                            id="confirm-run-title"
                            className="font-display text-base font-semibold text-slate-900"
                        >
                            Generate pay run?
                        </h4>
                        <p className="mt-1 text-sm text-slate-600">
                            This will generate the pay run for{' '}
                            <span className="font-bold text-red-400">{monthLabel}</span>. Are you
                            sure?
                        </p>
                    </div>
                </div>
                <div className="mt-5 flex justify-end gap-2">
                    <button
                        onClick={onCancel}
                        className="px-3 py-1.5 text-sm font-medium text-slate-600 hover:text-slate-900 hover:bg-slate-100 rounded-md"
                    >
                        Cancel
                    </button>
                    <button
                        onClick={onConfirm}
                        className="px-3 py-1.5 text-sm font-medium text-white bg-brand-600 hover:bg-brand-700 rounded-md"
                    >
                        Generate
                    </button>
                </div>
            </div>
        </div>
    );
}

/**
 * Destructive confirmation dialog for cleaning a month's run data. Deletes the
 * run, its files, and its payment summaries — so the wording is explicit about
 * what's removed and the confirm button is styled red. Config (subcontractors,
 * referrers) is untouched.
 */
function ConfirmCleanDialog({ open, monthLabel, onCancel, onConfirm }) {
    if (!open) return null;
    return (
        <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-4"
            onClick={onCancel}
            role="presentation"
        >
            <div
                className="w-full max-w-md bg-amber-50 rounded-lg shadow-xl border border-amber-200 p-5"
                onClick={(e) => e.stopPropagation()}
                role="dialog"
                aria-modal="true"
                aria-labelledby="confirm-clean-title"
            >
                <div className="flex items-start gap-3">
                    <AlertTriangle size={20} className="mt-0.5 flex-shrink-0 text-red-500" />
                    <div>
                        <h4
                            id="confirm-clean-title"
                            className="font-display text-base font-semibold text-slate-900"
                        >
                            Clean run data?
                        </h4>
                        <p className="mt-1 text-sm text-slate-600">
                            This permanently deletes the commission run for{' '}
                            <span className="font-bold text-red-400">{monthLabel}</span> — its
                            uploaded files, generated workbooks, and payment summaries. Your
                            subcontractors and referrers are not affected. This cannot be undone.
                        </p>
                    </div>
                </div>
                <div className="mt-5 flex justify-end gap-2">
                    <button
                        onClick={onCancel}
                        className="px-3 py-1.5 text-sm font-medium text-slate-600 hover:text-slate-900 hover:bg-slate-100 rounded-md"
                    >
                        Cancel
                    </button>
                    <button
                        onClick={onConfirm}
                        className="px-3 py-1.5 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-md"
                    >
                        Clean run data
                    </button>
                </div>
            </div>
        </div>
    );
}

/**
 * Confirmation for deleting an uploaded input file.
 *
 * Replaces `window.confirm('Delete this file? This cannot be undone.')`, which
 * was unstyled, could not name which file was going, and is suppressible: once a
 * user ticks Chrome's "prevent this page from creating additional dialogs", every
 * later delete fires instantly with no prompt at all.
 *
 * Red like Clean run data, because this one genuinely destroys something — the
 * file is removed from S3.
 */
function ConfirmDeleteFileDialog({ file, monthLabel, onCancel, onConfirm }) {
    if (!file) return null;
    return (
        <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-4"
            onClick={onCancel}
            role="presentation"
        >
            <div
                className="w-full max-w-md bg-white rounded-lg shadow-xl border border-slate-200 p-5"
                onClick={(e) => e.stopPropagation()}
                role="dialog"
                aria-modal="true"
                aria-labelledby="confirm-delete-file-title"
            >
                <div className="flex items-start gap-3">
                    <AlertTriangle size={20} className="mt-0.5 flex-shrink-0 text-red-500" />
                    <div className="min-w-0">
                        <h4
                            id="confirm-delete-file-title"
                            className="font-display text-base font-semibold text-slate-900"
                        >
                            Delete {file.label}?
                        </h4>
                        <p className="mt-1 text-sm text-slate-600 break-words">
                            <span className="font-medium text-slate-800">{file.name}</span>{' '}
                            will be removed from {monthLabel}. This cannot be undone —
                            you would need to upload the file again to run the month.
                        </p>
                    </div>
                </div>
                <div className="mt-5 flex justify-end gap-2">
                    <button
                        onClick={onCancel}
                        className="px-3 py-1.5 text-sm font-medium text-slate-600 hover:text-slate-900 hover:bg-slate-100 rounded-md"
                    >
                        Cancel
                    </button>
                    <button
                        onClick={onConfirm}
                        className="px-3 py-1.5 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-md"
                    >
                        Delete file
                    </button>
                </div>
            </div>
        </div>
    );
}

/**
 * Confirmation for importing rate splits.
 *
 * Amber, not red: unlike Clean run data this destroys nothing and is repeatable.
 * What it warrants a stop for is that it writes to the loan rate store shared by
 * every future run, and that the operator is about to press a button on a month
 * they may have meant to actually RUN — so the dialog leads with the fact that no
 * commission will be calculated.
 */
function ConfirmImportSplitsDialog({ open, monthLabel, onCancel, onConfirm }) {
    if (!open) return null;
    return (
        <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-4"
            onClick={onCancel}
            role="presentation"
        >
            <div
                className="w-full max-w-md bg-amber-50 rounded-lg shadow-xl border border-amber-200 p-5"
                onClick={(e) => e.stopPropagation()}
                role="dialog"
                aria-modal="true"
                aria-labelledby="confirm-import-splits-title"
            >
                <div className="flex items-start gap-3">
                    <AlertTriangle size={20} className="mt-0.5 flex-shrink-0 text-amber-600" />
                    <div>
                        <h4
                            id="confirm-import-splits-title"
                            className="font-display text-base font-semibold text-slate-900"
                        >
                            Import rate splits for {monthLabel}?
                        </h4>
                        <p className="mt-1 text-sm text-slate-600">
                            This reads the uploaded CRM&rsquo;s upfront and trail split
                            rates, plus the sub-contractor and referrer names, into the
                            loan rate store — used by{' '}
                            <span className="font-medium text-slate-700">every future
                            run</span> to price trail and reverse clawbacks.
                        </p>
                        <p className="mt-2 text-sm text-slate-600">
                            <span className="font-semibold text-amber-800">No commission
                            is calculated and nothing is paid.</span> No reports or
                            invoices are produced and {monthLabel} stays{' '}
                            <span className="font-medium">Pending</span>. Use this only
                            for a historical month already paid outside this system — if
                            you meant to calculate commission, use{' '}
                            <span className="font-medium text-slate-700">Run</span>{' '}
                            instead.
                        </p>
                        <p className="mt-2 text-xs text-slate-500">
                            Rates already stored for these loans will be restated to
                            match this file. Safe to repeat.
                        </p>
                    </div>
                </div>
                <div className="mt-5 flex justify-end gap-2">
                    <button
                        onClick={onCancel}
                        className="px-3 py-1.5 text-sm font-medium text-slate-600 hover:text-slate-900 hover:bg-slate-100 rounded-md"
                    >
                        Cancel
                    </button>
                    <button
                        onClick={onConfirm}
                        className="px-3 py-1.5 text-sm font-medium text-white bg-amber-600 hover:bg-amber-700 rounded-md"
                    >
                        Import rate splits
                    </button>
                </div>
            </div>
        </div>
    );
}

/**
 * Hit the backend's download endpoint, then trigger a browser save using
 * the filename the server provides in {@code Content-Disposition}. Bytes
 * flow API → axios blob → object URL → anchor click → user's downloads.
 *
 * <p>The object URL is revoked on a short timeout to avoid leaking memory
 * across many downloads (Chrome tends to be patient about freeing them
 * but Safari is stricter).</p>
 *
 * @param fallbackName used if the response Content-Disposition is
 *   missing or unparseable — typically the {@code file.name} we already
 *   have in state from the run detail.
 */
async function downloadFileAndSave(orgUuid, year, month, fileId, fallbackName) {
    const res = await commissionReportsApi.downloadFile(
        orgUuid, year, month, fileId);

    const filename = parseContentDispositionFilename(
        res.headers?.['content-disposition']) ?? fallbackName ?? 'download';

    const blob = res.data;
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => window.URL.revokeObjectURL(url), 1000);
}

/**
 * Pull the filename out of a {@code Content-Disposition} header value.
 * Prefers the RFC 5987 {@code filename*=UTF-8''...} variant (handles
 * non-ASCII names correctly via percent-decoding), falls back to the
 * legacy quoted {@code filename="..."}. Returns null when neither parses.
 */
function parseContentDispositionFilename(header) {
    if (!header) return null;
    // RFC 5987 form: filename*=UTF-8''percent-encoded
    const star = /filename\*\s*=\s*UTF-8''([^;]+)/i.exec(header);
    if (star) {
        try { return decodeURIComponent(star[1]); }
        catch { /* fall through to legacy form */ }
    }
    // Legacy quoted form: filename="X.xlsx" or filename=X.xlsx
    const legacy = /filename\s*=\s*"?([^";]+)"?/i.exec(header);
    return legacy ? legacy[1] : null;
}

// ── Output files (read-only) ───────────────────────────────────────────

function OutputFilesSection({ orgUuid, year, month, files, status, onChange }) {
    // The RCTI whose Email button was clicked, if any. One dialog at a time:
    // it either asks to confirm the saved address, or says there is none.
    const [emailTarget, setEmailTarget] = useState(null);
    const [sendingId, setSendingId] = useState(null);
    // The outcome of the last send — shown as an in-app notice, never a
    // browser alert: the message names a payee and an address, and has to
    // stay on screen and readable until it is dismissed.
    const [emailNotice, setEmailNotice] = useState(null);

    const handleSend = async () => {
        const target = emailTarget;
        setEmailTarget(null);
        if (!target) return;
        setSendingId(target.id);
        try {
            const sent = await commissionReportsApi.emailRcti(orgUuid, year, month, target.id);
            setEmailNotice({
                tone: 'success',
                title: `Sent to ${sent.recipientName}`,
                message: `${target.name} was emailed to ${sent.sentTo}.`,
            });
            onChange?.();
        } catch (err) {
            setEmailNotice({
                tone: 'error',
                title: 'The RCTI was not sent',
                message: err.response?.data?.message ?? err.message,
            });
        } finally {
            setSendingId(null);
        }
    };

    return (
        <section>
            <div className="mb-3">
                <h3 className="font-display text-base font-semibold text-slate-900">
                    Generated reports
                </h3>
                <p className="text-xs text-slate-500">
                    Commission reports produced by the run. Each RCTI can be emailed to the
                    payee it is addressed to, at the address saved on their business entity.
                </p>
            </div>

            <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
                {files.length === 0 ? (
                    <div className="px-4 py-8 flex flex-col items-center gap-2 text-center">
                        <Inbox size={20} className="text-slate-300" />
                        <p className="text-sm text-slate-400">
                            {status === 'IN_PROGRESS'
                                ? 'Reports will appear here when the run completes.'
                                : 'No reports generated yet.'}
                        </p>
                    </div>
                ) : (
                    <ul className="divide-y divide-slate-100">
                        {files.map((f) => (
                            <FileRow
                                key={f.id}
                                file={f}
                                onDownload={() => downloadFileAndSave(
                                    orgUuid, year, month, f.id, f.name)}
                                onEmail={f.rcti ? () => setEmailTarget(f) : null}
                                sending={sendingId === f.id}
                            />
                        ))}
                    </ul>
                )}
            </div>

            <EmailRctiDialog
                file={emailTarget}
                onCancel={() => setEmailTarget(null)}
                onConfirm={handleSend}
            />
            <NoticeDialog notice={emailNotice} onClose={() => setEmailNotice(null)} />
        </section>
    );
}

/**
 * Before an RCTI is emailed. Two shapes, decided by what the server already
 * told us about the payee:
 *
 *   • an address is saved — confirm, showing the name AND the address, because
 *     a tax invoice sent to the wrong person is not recalled by a second click;
 *   • no address — an error in the app's own dialog naming the payee and where
 *     the address is entered. There is deliberately no field to type one here:
 *     the address on the invoice and the address it goes to must be the same
 *     saved value.
 */
function EmailRctiDialog({ file, onCancel, onConfirm }) {
    if (!file) return null;
    const name = file.rctiRecipientName ?? 'this payee';
    const email = file.rctiRecipientEmail;
    // An RCTI whose payee the server could not determine — the entity was
    // deleted or renamed since the run. There is nobody to send to.
    const unresolved = !file.rctiRecipientName;
    const titleId = 'email-rcti-title';

    return (
        <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-4"
            onClick={onCancel}
            role="presentation"
        >
            <div
                className="w-full max-w-md bg-white rounded-lg shadow-xl border border-slate-200 p-5"
                onClick={(e) => e.stopPropagation()}
                role="dialog"
                aria-modal="true"
                aria-labelledby={titleId}
            >
                {unresolved ? (
                    <>
                        <div className="flex items-start gap-3">
                            <AlertCircle size={20} className="mt-0.5 flex-shrink-0 text-red-500" />
                            <div className="min-w-0">
                                <h4 id={titleId} className="font-display text-base font-semibold text-slate-900">
                                    This RCTI cannot be matched to a payee
                                </h4>
                                <p className="mt-1 text-sm text-slate-600 break-words">
                                    <span className="font-medium text-slate-800">{file.name}</span>
                                    {' '}is addressed to a business entity that is no longer in
                                    your configuration under that name, so there is nobody to
                                    send it to. Restore the entity under Configuration → Business
                                    entities, or re-run the month.
                                </p>
                            </div>
                        </div>
                        <div className="mt-5 flex justify-end gap-2">
                            <button
                                onClick={onCancel}
                                className="px-3 py-1.5 text-sm font-medium text-slate-600 hover:text-slate-900 hover:bg-slate-100 rounded-md"
                            >
                                Close
                            </button>
                        </div>
                    </>
                ) : email ? (
                    <>
                        <div className="flex items-start gap-3">
                            <Mail size={20} className="mt-0.5 flex-shrink-0 text-brand-600" />
                            <div className="min-w-0">
                                <h4 id={titleId} className="font-display text-base font-semibold text-slate-900">
                                    Email this RCTI to {name}?
                                </h4>
                                <p className="mt-1 text-sm text-slate-600 break-words">
                                    <span className="font-medium text-slate-800">{file.name}</span>
                                    {' '}will be sent as an attachment to{' '}
                                    <span className="font-medium text-slate-800">{email}</span>,
                                    the address saved on their business entity.
                                </p>
                                {file.emailedTo && (
                                    <p className="mt-2 text-xs text-slate-500">
                                        Already sent to {file.emailedTo} on {formatDateTime(file.emailedAt)}.
                                        Sending again is fine.
                                    </p>
                                )}
                            </div>
                        </div>
                        <div className="mt-5 flex justify-end gap-2">
                            <button
                                onClick={onCancel}
                                className="px-3 py-1.5 text-sm font-medium text-slate-600 hover:text-slate-900 hover:bg-slate-100 rounded-md"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={onConfirm}
                                className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-white bg-brand-600 hover:bg-brand-700 rounded-md"
                            >
                                <Send size={14} />
                                Send
                            </button>
                        </div>
                    </>
                ) : (
                    <>
                        <div className="flex items-start gap-3">
                            <AlertCircle size={20} className="mt-0.5 flex-shrink-0 text-red-500" />
                            <div className="min-w-0">
                                <h4 id={titleId} className="font-display text-base font-semibold text-slate-900">
                                    No email address is saved for {name}
                                </h4>
                                <p className="mt-1 text-sm text-slate-600 break-words">
                                    This RCTI is addressed to{' '}
                                    <span className="font-medium text-slate-800">{name}</span>, but
                                    their business entity has no email address, so it cannot be
                                    sent. Add one under Configuration → Business entities, then
                                    try again.
                                </p>
                            </div>
                        </div>
                        <div className="mt-5 flex justify-end gap-2">
                            <button
                                onClick={onCancel}
                                className="px-3 py-1.5 text-sm font-medium text-slate-600 hover:text-slate-900 hover:bg-slate-100 rounded-md"
                            >
                                Close
                            </button>
                            <a
                                href="/configuration"
                                className="px-3 py-1.5 text-sm font-medium text-white bg-brand-600 hover:bg-brand-700 rounded-md"
                            >
                                Open configuration
                            </a>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
}

/**
 * The app's own message box, for an outcome the operator has to read: a send
 * that went, or one the server refused. A browser alert would do neither well
 * — it cannot be styled, can be suppressed per site, and disappears with the
 * message — so this is used instead.
 */
function NoticeDialog({ notice, onClose }) {
    if (!notice) return null;
    const error = notice.tone === 'error';
    return (
        <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-4"
            onClick={onClose}
            role="presentation"
        >
            <div
                className="w-full max-w-md bg-white rounded-lg shadow-xl border border-slate-200 p-5"
                onClick={(e) => e.stopPropagation()}
                role="dialog"
                aria-modal="true"
                aria-labelledby="notice-title"
            >
                <div className="flex items-start gap-3">
                    {error
                        ? <AlertCircle size={20} className="mt-0.5 flex-shrink-0 text-red-500" />
                        : <CheckCircle2 size={20} className="mt-0.5 flex-shrink-0 text-emerald-600" />}
                    <div className="min-w-0">
                        <h4 id="notice-title" className="font-display text-base font-semibold text-slate-900">
                            {notice.title}
                        </h4>
                        <p className="mt-1 text-sm text-slate-600 break-words">{notice.message}</p>
                    </div>
                </div>
                <div className="mt-5 flex justify-end">
                    <button
                        onClick={onClose}
                        className="px-3 py-1.5 text-sm font-medium text-white bg-brand-600 hover:bg-brand-700 rounded-md"
                    >
                        Close
                    </button>
                </div>
            </div>
        </div>
    );
}

function FileRow({ file, onDownload, onDelete, onEmail, sending }) {
    const [downloading, setDownloading] = useState(false);

    const handleDownload = async () => {
        if (!onDownload || downloading) return;
        setDownloading(true);
        try {
            await onDownload();
        } catch (err) {
            // No inline error UI on the row — too cramped. Browser shows
            // a failed network request, which is sufficient signal for
            // the user to retry. Log so support can see it.
            // eslint-disable-next-line no-console
            console.error('File download failed:', err);
        } finally {
            setDownloading(false);
        }
    };

    return (
        <li className="px-4 py-3 flex items-center gap-3">
            <FileSpreadsheet size={18} className="text-emerald-600 flex-shrink-0" />
            <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-slate-900 truncate">{file.name}</p>
                <p className="text-xs text-slate-500 mt-0.5">
                    {formatBytes(file.sizeBytes)} · {formatDateTime(file.timestamp)}
                    {file.emailedTo && (
                        <span className="text-emerald-700">
                            {' · '}Emailed to {file.emailedTo} {formatDateTime(file.emailedAt)}
                        </span>
                    )}
                </p>
            </div>
            <span className="text-xs text-slate-400 font-mono whitespace-nowrap">
                {KIND_LABEL[file.kind] ?? file.kind}
            </span>
            {onEmail && (
                // Only on RCTIs: a payment run or per-loan calc is addressed to
                // nobody. The button is enabled even when no address is saved —
                // the dialog is where that is explained, not a greyed-out icon.
                <button
                    onClick={onEmail}
                    disabled={sending}
                    className="flex items-center gap-1 px-2 py-1 text-xs text-slate-500 hover:text-brand-700 hover:bg-slate-100 rounded-md disabled:opacity-40 disabled:cursor-not-allowed whitespace-nowrap"
                    title={file.rctiRecipientEmail
                        ? `Email to ${file.rctiRecipientName} (${file.rctiRecipientEmail})`
                        : file.rctiRecipientName
                            ? `No email address is saved for ${file.rctiRecipientName}`
                            : 'Email this RCTI to its payee'}
                >
                    {sending ? <Loader2 size={14} className="animate-spin" /> : <Mail size={14} />}
                    {sending ? 'Sending…' : 'Email'}
                </button>
            )}
            {onDownload && (
                <button
                    onClick={handleDownload}
                    disabled={downloading}
                    className="text-slate-400 hover:text-brand-600 p-1 rounded disabled:opacity-40 disabled:cursor-not-allowed"
                    title="Download file"
                >
                    {downloading
                        ? <Loader2 size={16} className="animate-spin" />
                        : <Download size={16} />}
                </button>
            )}
            {onDelete && (
                <button
                    onClick={onDelete}
                    className="text-slate-400 hover:text-red-600 p-1 rounded"
                    title="Delete file"
                >
                    <Trash2 size={16} />
                </button>
            )}
        </li>
    );
}

function formatBytes(bytes) {
    if (bytes == null) return '—';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function formatDateTime(iso) {
    if (!iso) return '—';
    const d = new Date(iso);
    return d.toLocaleString(undefined, {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
    });
}